/**
 * Passy — the studio's status face.
 *
 * From the kit: "He is a status face, not a host: he shows how the work is
 * going and then gets out of the way. He never talks." So he appears where
 * something has a state — the proposal form while it submits, the 404 — and
 * nowhere decorative.
 *
 * ── Four rectangles, rebuilt from the numbers ─────────────────────────────
 * The kit gives Passy as measurements on a 12-unit grid rather than as
 * artwork, precisely so he can be redrawn at any size from the spec alone.
 * Those numbers are the constants below, on a 100×100 viewBox:
 *
 *   square   the whole box, no rounding, no outline
 *   eyes     10% wide × 18% tall, at 27% and 63% from the left, 27% from top
 *   band     the bottom 30%, #2E9C58, always
 *
 * A closed eye is the same eye flattened to a bar on its own centre line,
 * which is why EYE_CLOSED_H exists rather than a second set of coordinates.
 *
 * ── The state we do not ship ──────────────────────────────────────────────
 * The kit defines a fifth state, `failing`, and restricts it: "never on a
 * client's live site, never in a proposal". A form error is not a failing
 * build, and the brand's own rule is that red and amber appear nowhere. So a
 * submit that fails shows the default face and an error message in words —
 * `failing` is deliberately not implemented here.
 */
import styles from './Passy.module.css';

export type PassyState =
  /** Default. Both eyes open, green band. */
  | 'passing'
  /** Loading — deploys, form submits, anything over a second. Dots cycle. */
  | 'building'
  /** A wink, right eye only. Only after something real shipped. */
  | 'shipped'
  /** Nothing running: eyes closed, the band goes quiet. */
  | 'idle';

const EYE_W = 10;
const EYE_H = 18;
const EYE_Y = 27;
const EYE_X_LEFT = 27;
const EYE_X_RIGHT = 63;
const EYE_CLOSED_H = 3;
const BAND_Y = 70;
const BAND_H = 30;

interface PassyProps {
  state?: PassyState;
  /** Rendered size in pixels. The kit's floor is 16; below that use the dot. */
  size?: number;
  /** On an ink ground the square inverts to bone, or the band is swallowed. */
  onInk?: boolean;
  /**
   * What a screen reader should say. Passed explicitly because the same face
   * means different things in different places, and `null` marks him as
   * decoration beside text that already says it.
   */
  label?: string | null;
}

/** One eye, open or closed. Closed is the open eye flattened onto its centre. */
function Eye({ x, closed, fill }: { x: number; closed: boolean; fill: string }) {
  return closed ? (
    <rect
      x={x}
      y={EYE_Y + (EYE_H - EYE_CLOSED_H) / 2}
      width={EYE_W}
      height={EYE_CLOSED_H}
      fill={fill}
    />
  ) : (
    <rect x={x} y={EYE_Y} width={EYE_W} height={EYE_H} fill={fill} />
  );
}

export function Passy({ state = 'passing', size = 48, onInk = false, label }: PassyProps) {
  const square = onInk ? 'var(--bone)' : 'var(--ink)';
  const eye = onInk ? 'var(--ink)' : 'var(--bone)';

  // Idle is the only state where the band is not green: nothing is running, so
  // there is no status to report and the band drops to a hairline grey.
  const band = state === 'idle' ? 'var(--line)' : onInk ? 'var(--pass-up)' : 'var(--pass)';

  const leftClosed = state === 'idle' || state === 'shipped';
  const rightClosed = state === 'idle';

  return (
    <svg
      className={styles.passy}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      role={label ? 'img' : 'presentation'}
      aria-label={label ?? undefined}
      aria-hidden={label ? undefined : true}
    >
      <rect x="0" y="0" width="100" height="100" fill={square} />
      <Eye x={EYE_X_LEFT} closed={leftClosed} fill={eye} />
      <Eye x={EYE_X_RIGHT} closed={rightClosed} fill={eye} />
      <rect x="0" y={BAND_Y} width="100" height={BAND_H} fill={band} />

      {/*
        Building: three dots cycling left to right across the band, 1.1s, as the
        kit specifies. They sit on the band rather than replacing it, because
        the band is the status and the animation is only how it reads as busy.
      */}
      {state === 'building' ? (
        <g className={styles.dots}>
          {[28, 50, 72].map((cx, i) => (
            <circle
              key={cx}
              cx={cx}
              cy={BAND_Y + BAND_H / 2}
              r="5"
              fill={onInk ? 'var(--ink)' : 'var(--bone)'}
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </g>
      ) : null}
    </svg>
  );
}
